import { createContext, PropsWithChildren, useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { buildPlayer, buildSeason, findRelevantSeason } from './utils'
import {
  arcadeSeasonsQueryKey,
  useArcadeSeasonsQuery
} from '@/api/resources/arcade/season/get'
import { ArcadeContextType } from './types'
import {
  useArcadeSeasonEnded,
  useArcadeSeasonStarted
} from './hooks/transitioned'
import { logSessionEvent } from '@/util/logger'
import { allArcadeLeaderboardsQueryKey } from '@/api/resources/arcade/rank/get'
import { path as userAssetsPath } from '@/api/resources/user/assets/get'

const ArcadeContext = createContext<Partial<ArcadeContextType>>({})

export const useArcade = () => useContext(ArcadeContext)

const SEASON_STATUS_CHANGED_TIMEOUT = 2000 as const

export const ArcadeProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient()

  const { arcadeUser, user, fetchUser, isAuthenticated } = useAuth()
  const { data: seasonsData } = useArcadeSeasonsQuery({
    enabled: !isAuthenticated
  })

  const allArcadeSeasons = seasonsData?.data

  const arcadeSeason =
    arcadeUser?.season || findRelevantSeason(allArcadeSeasons)

  const season = arcadeSeason ? buildSeason(arcadeSeason) : null
  const player = arcadeUser ? buildPlayer(user, arcadeUser, season) : null

  const handleSeasonStatusChanged = () => {
    setTimeout(() => {
      fetchUser().then()
      queryClient.invalidateQueries(arcadeSeasonsQueryKey).then()
      queryClient.invalidateQueries([userAssetsPath]).then()
      queryClient
        .invalidateQueries(allArcadeLeaderboardsQueryKey, { exact: false })
        .then()
    }, SEASON_STATUS_CHANGED_TIMEOUT)
  }

  const handleSeasonStarted = () => {
    logSessionEvent(`[arcade] season ${season?.name} started`)
    handleSeasonStatusChanged()
  }

  const handleSeasonEnded = () => {
    logSessionEvent(`[arcade] season ${season?.name} ended`)
    handleSeasonStatusChanged()
  }

  useArcadeSeasonStarted({ season, onSeasonStarted: handleSeasonStarted })
  useArcadeSeasonEnded({ season, onSeasonEnded: handleSeasonEnded })

  const isArcadeLoaded = !!season

  const value = { isArcadeLoaded, season, player }

  return (
    <ArcadeContext.Provider value={value}>{children}</ArcadeContext.Provider>
  )
}
